import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stack } from 'new/elements/Stack';
import { Box } from 'new/elements/Box';
import { Breadcrumbs } from '../Breadcrumbs';
import { FileList } from '../FileList';
import type { BreadcrumbTreeViewProps } from './types';
import { usePathResolutionV2 } from './hooks/usePathResolutionV2';
import { useEmptyFolderGate } from '../hooks/useEmptyFolderGate';

import { TextField, InputAdornment } from '@mui/material';
import { ReactComponent as CancelIcon } from 'new/components/icons/fi-sr-cross-circle.svg';
import { Search as SearchIcon } from '@mui/icons-material';
import { ReactComponent as CheckIcon } from 'new/components/icons/check.svg';
import SelectedLocationPathInput from 'js/components/data_sources/add_ds_form/SelectedLocationPathInput/SelectedLocationPathInput';
import classes from './BreadcrumbTreeView.module.scss';
import { ReactComponent as RefreshIconRotate } from 'new/components/icons/fi-rr-refresh-rotate.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh.svg';
import { ReactComponent as WarningIcon } from 'new/components/icons/fi-rr-warning.svg';
import Typography from 'new/elements/TypographySupport';
import MentionsBasicEditor from 'js/components/elements/editors/draftjs/MentionsBasicEditor';
import { PathWarnings } from './PathWarnings';
import { FolderOpen as FolderOpenIcon } from '@mui/icons-material';

export const BreadcrumbTreeView: React.FC<BreadcrumbTreeViewProps> = (props) => {
  const hookResult = usePathResolutionV2({
    data: props.data,
    selectedNodeId: props.selectedNodeId,
    nodeKeyName: props.nodeKeyName || 'children',
    isLoadingData: props.isLoadingData,
    lastUpdatedAt: props.lastUpdatedAt,
    onNodeExpandClicked: props.onNodeExpandClicked,
    onNodeSelected: props.onNodeSelected,
    stopAtParent: props.stopAtParent,
    pathInputValue: props.pathInputValue,
    uiDisplayPath: props.uiDisplayPath,
    isIDBased: props.isIDBased,
    isConnectorWithLeafIdOnly: props.isConnectorWithLeafIdOnly,
    uiIdPath: props.uiIdPath,
    onIdPathResolved: props.onIdPathResolved,
  });
  return <BreadcrumbTreeViewInner {...props} hookResult={hookResult} />;
};

// Type for the hook result
interface PathResolutionResult {
  path: any[];
  currentData: any;
  handleBreadcrumbClick: (index: number) => void;
  handleFolderClick: (node: any) => void;
  handleFolderNavigate: (node: any) => void;
  showBreadcrumbs: boolean;
  handleEditClick: () => void;
  handleCheckClick: () => void;
  navigateToPath: (path: string, options?: { forceToLeaf?: boolean }) => void;
  resetNavigationState: () => void;
  lastNavigateFailed: boolean;
  isResolvingPath: boolean;
  lastMissingSegment: string | null;
}

interface BreadcrumbTreeViewInnerProps extends BreadcrumbTreeViewProps {
  hookResult: PathResolutionResult;
}

/**
 * Inner component - shared rendering logic used by both V1 and V2
 */
const BreadcrumbTreeViewInner: React.FC<BreadcrumbTreeViewInnerProps> = ({
  data,
  fileIngestModifiedAfterMs,
  selectedNodeId,
  onNodePreview,
  onNodeSelected,
  onNodeExpandClicked,
  nodeKeyName = 'children',
  selectable = true,
  enableFileSelection = false,
  selectableFileMimeType,
  filesOnly = false,
  iconClasses,
  isLoadingData,
  currentPath = [],
  lastUpdatedAt,
  stopAtParent = false,
  search = '',
  onSearchChange,
  onPathInputChange,
  pathInputValue,
  pathInputReadOnly = false,
  pathInputPlaceholder,
  isLoadingProbeData,
  onRefreshTreeClicked,
  isDestination = false,
  allPrefixSuggestions,
  customPrefixEditorState,
  filteredPrefixSuggestions,
  handlePrefixEditorChange,
  handlePrefixEditorSearch,
  handlePrefixEditorKeyDown,
  stateUpdatedAt,
  uiDisplayPath,
  isIDBased = false,
  hookResult,
}) => {
  const [pathInputDraft, setPathInputDraft] = useState<string>(pathInputValue || '');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Transient banner state
  const [showTransientBanner, setShowTransientBanner] = useState(false);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSelectingFromList, setIsSelectingFromList] = useState<boolean>(false);

  // Gate: suppress empty-state after certain actions
  const suppressEmptyUntilRef = useRef<number>(0);
  // Stability: require empty-state to remain true for a window before rendering
  const emptyStableSinceRef = useRef<number | null>(null);
  // Keep last non-empty list to avoid visual blank during transitions
  const lastNonEmptyDataRef = useRef<any[] | null>(null);
  // Local refs used across the component
  const latestDraftRef = useRef<string>(pathInputValue || '');
  const initialRootItemsRef = useRef<any[] | null>(null);
  const pendingNavigationRef = useRef<boolean>(false);

  // Destructure hook result
  const {
    path,
    currentData,
    handleBreadcrumbClick,
    handleFolderClick,
    handleFolderNavigate,
    showBreadcrumbs,
    handleEditClick,
    handleCheckClick,
    navigateToPath,
    resetNavigationState,
    lastNavigateFailed,
    isResolvingPath,
    lastMissingSegment,
  } = hookResult;

  // Determine if children are actually loaded (known) for the current folder
  const childrenStatus = React.useMemo(() => {
    if (path.length === 0) return { childrenKnown: false, childCount: -1 };
    const targetId = (path[path.length - 1] as any)?.id;
    if (!targetId) return { childrenKnown: false, childCount: -1 };
    const items = Array.isArray(data) ? (data as any[]) : [data as any];
    // Defensive: the probe tree can be cyclic (same JS object reused as its own descendant)
    // due to occasional backend payload anomalies + UI normalization/deduping by id.
    // Guard by object identity (WeakSet) to avoid stack overflows; this does NOT treat
    // repeated names/paths (e.g. S3 a/a/a) as cycles.
    const visited = new WeakSet<object>();
    const findNodeById = (nodes: any[]): any | null => {
      for (const node of nodes) {
        if (!node) continue;
        if (typeof node === 'object') {
          if (visited.has(node)) continue;
          visited.add(node);
        }
        if (node?.id === targetId) return node;
        const children = node?.[nodeKeyName];
        if (Array.isArray(children)) {
          const found = findNodeById(children as any[]);
          if (found) return found;
        }
      }
      return null;
    };
    const node = findNodeById(items);
    if (!node) return { childrenKnown: false, childCount: -1 };
    const children = node?.[nodeKeyName];
    if (Array.isArray(children)) return { childrenKnown: true, childCount: children.length };
    // Some connectors (e.g., Google Drive) can return an object for children (e.g., {}). Treat as known-empty
    if (children && typeof children === 'object') return { childrenKnown: true, childCount: 0 };
    return { childrenKnown: false, childCount: -1 };
  }, [data, nodeKeyName, path]);

  const doesFirstSegMatchInitialRoot = React.useCallback((value: string) => {
    const trimmed = (value || '').trim();
    if (!trimmed || !initialRootItemsRef.current) return false;
    const firstSeg = trimmed.split('/').filter(Boolean)[0];
    if (!firstSeg) return false;
    const firstLower = firstSeg.toLowerCase();
    return initialRootItemsRef.current.some((item) => {
      const display = (
        (item as any).displayName ||
        (item as any).display_name ||
        (item as any).name ||
        ''
      ).toLowerCase();
      const idLower = String((item as any).id || '').toLowerCase();
      return display === firstLower || idLower === firstLower;
    });
  }, []);

  // Special case: some connectors (e.g., Dropbox/FTP) expose a root item named '/'.
  // In those cases, any typed path is implicitly under this root, so the "root segment"
  // should always be considered valid to avoid spurious "Path not found" warnings
  // when toggling to input or typing paths like '/folder'.
  const hasSlashRoot = (() => {
    const items = initialRootItemsRef.current;
    if (!items || items.length === 0) return false;
    for (const item of items) {
      const display = (
        (item as any).displayName ||
        (item as any).display_name ||
        (item as any).name ||
        ''
      )
        .trim()
        .toLowerCase();
      const idLower = String((item as any).id || '')
        .trim()
        .toLowerCase();
      if (display === '/' || idLower === '/') return true;
    }
    return false;
  })();

  // Shared warning derivations for banners and inline helpers
  const draftOrCommitted = (pathInputDraft || pathInputValue || '').trim();
  const hasInitialRoot = Boolean(
    initialRootItemsRef.current && initialRootItemsRef.current.length > 0,
  );
  const rootSegValid = hasInitialRoot
    ? hasSlashRoot
      ? true
      : doesFirstSegMatchInitialRoot(draftOrCommitted)
    : true;
  const isUserTyping = !isSelectingFromList && !showBreadcrumbs && !!draftOrCommitted;
  const showPathNotFoundTyping =
    isUserTyping && !!draftOrCommitted && hasInitialRoot && !rootSegValid;

  // Show banner when navigation fails (path doesn't exist)
  useEffect(() => {
    if (lastNavigateFailed) {
      if (bannerTimerRef.current) {
        clearTimeout(bannerTimerRef.current);
        bannerTimerRef.current = null;
      }
      setShowTransientBanner(true);
      bannerTimerRef.current = setTimeout(() => {
        setShowTransientBanner(false);
        bannerTimerRef.current = null;
      }, 10000);
    }
  }, [lastNavigateFailed]);

  // Hide banner on typing or new navigation attempt
  const hideBanner = React.useCallback(() => {
    if (bannerTimerRef.current) {
      clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = null;
    }
    setShowTransientBanner(false);
  }, []);

  // Store the first list of folders rendered on initial load (root)
  useEffect(() => {
    if (!initialRootItemsRef.current) {
      const isAtRoot = path.length === 0;
      const asArray = Array.isArray(currentData) ? currentData : [];
      if (isAtRoot && asArray.length > 0) {
        initialRootItemsRef.current = asArray;
      }
    }
  }, [currentData, path.length]);

  // Wrapper to track when user is selecting from list
  const handleFolderClickWithSelection = React.useCallback(
    (node: any) => {
      // If children are unknown or reported as 0 at click time, suppress empty-state longer
      const childrenKnown = Array.isArray(node?.[nodeKeyName]);
      const childCount = childrenKnown ? node[nodeKeyName].length : -1;
      const baseDelayMs = childCount === 0 ? 0 : 200;
      // Do not suppress when we already know it's empty (childCount === 0)
      const clickDelayMs = !childrenKnown ? 700 : childCount === 0 ? 0 : 250;
      const now = Date.now();
      const untilTs = now + Math.max(baseDelayMs, clickDelayMs);
      if (childCount === 0) {
        // Clear any prior suppression immediately for known-empty so empty-state can render
        suppressEmptyUntilRef.current = now;
      } else if (untilTs > suppressEmptyUntilRef.current) {
        suppressEmptyUntilRef.current = untilTs;
      }
      setIsSelectingFromList(true);

      handleFolderClick(node);
      // Reset flag after a short delay to allow for state updates
      setTimeout(
        () => {
          setIsSelectingFromList(false);
        },
        childCount === 0 ? 10 : 100,
      );
    },
    [handleFolderClick],
  );

  const prevShowBreadcrumbsRefLocal = useRef<boolean>(showBreadcrumbs);
  useEffect(() => {
    const wasShowing = prevShowBreadcrumbsRefLocal.current;
    prevShowBreadcrumbsRefLocal.current = showBreadcrumbs;
    // Only sync input draft when we transition into manual input mode
    if (wasShowing && !showBreadcrumbs) {
      setPathInputDraft(pathInputValue || '');
      // Keep latestDraftRef in sync with what the user sees in the input
      latestDraftRef.current = pathInputValue || '';
      // Reset navigation failure state when switching to input mode
      resetNavigationState();
    }
  }, [showBreadcrumbs, pathInputValue, resetNavigationState]);

  const handlePathInputChange = (pathData: any, nodeId?: string) => {
    // Update local draft immediately for responsive typing
    const nextValue = pathData?.path ?? '';
    hideBanner();
    setPathInputDraft(nextValue);
    latestDraftRef.current = nextValue;

    // Reset selection flag when user starts typing
    setIsSelectingFromList(false);

    // Reset navigation failure state when user starts typing a new path
    if (nextValue !== pathInputValue) {
      // Clear any previous navigation failure state when user types something different
      resetNavigationState();
    }

    if (!onPathInputChange) return;

    // Debounce notifying parent to avoid resolving while user types
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (pathData === null) {
        onPathInputChange(null, null);
        return;
      }

      if (pathData && pathData.path) {
        const node = {
          id: pathData.path,
          name: pathData.name || pathData.path,
          path: pathData.path,
          type: 'folder' as const,
        };
        onPathInputChange(node, pathData.path);
      }
    }, 400);
  };

  const handleCheckClickWithNavigation = () => {
    // For read-only inputs (e.g., Google Drive), don't navigate unless in destination mode
    if (pathInputReadOnly && !isDestination) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      handleCheckClick();
      return;
    }
    // Prefer the visible draft first; fall back to latest ref, then committed
    const candidatePath = (pathInputDraft || latestDraftRef.current || pathInputValue || '').trim();

    // If there's an in-flight debounce, cancel and immediately commit current draft
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      if (onPathInputChange) {
        if (!candidatePath) {
          onPathInputChange(null, null);
        } else {
          const node = {
            id: candidatePath,
            name:
              candidatePath
                .split('/')
                .filter((s) => s.length > 0)
                .pop() || candidatePath,
            path: candidatePath,
            type: 'folder' as const,
          };
          onPathInputChange(node, candidatePath);
        }
      }
    }

    if (candidatePath) {
      // Keep input visible; only switch to breadcrumbs on success
      hideBanner();
      pendingNavigationRef.current = true;
      const forceToLeaf = isDestination || !showBreadcrumbs;

      // navigateToPath handles probing internally - don't call onNodeExpandClicked here
      // to avoid duplicate probe calls
      navigateToPath(candidatePath, { forceToLeaf });
      // Keep input visible until the hook flips to breadcrumbs on success
    } else {
      // Empty input: allow toggling back to breadcrumbs
      handleCheckClick();
    }
  };

  const handleRefreshClick = useCallback(() => {
    const mode = showBreadcrumbs ? 'breadcrumbs' : 'input';
    if (!onRefreshTreeClicked) return;
    const depth = Math.max(1, path.length);
    const node = path.length > 0 ? path[path.length - 1] : undefined;
    let pathString: string | undefined;
    if (mode === 'input') {
      const typedPath = (pathInputDraft || pathInputValue || '').trim();
      pathString = typedPath || undefined;
    } else if (path.length > 0) {
      const segments = path.map((p) => {
        if (isIDBased) return String(p.id || '');
        return String((p as any).displayName || (p as any).display_name || p.name || '');
      });
      pathString = segments.filter(Boolean).join('/') || undefined;
    }
    onRefreshTreeClicked({ node, nodeId: node?.id, path: pathString, depth });
  }, [showBreadcrumbs, path, pathInputDraft, pathInputValue, isIDBased, onRefreshTreeClicked]);

  // Switch to breadcrumbs only after a successful navigation completion
  useEffect(() => {
    if (!pendingNavigationRef.current) return;
    if (isResolvingPath) return;
    // Navigation finished
    if (!lastNavigateFailed) {
      handleCheckClick();
    }
    pendingNavigationRef.current = false;
  }, [isResolvingPath, lastNavigateFailed, handleCheckClick]);

  // Basic empty-state predicate based on render-time values
  const shouldShowEmpty =
    Array.isArray(currentData) &&
    currentData.length === 0 &&
    path.length > 0 &&
    !isLoadingData &&
    !isResolvingPath;

  useEffect(() => {
    if (Array.isArray(currentData) && currentData.length > 0) {
      lastNonEmptyDataRef.current = currentData as any[];
    }
  }, [currentData]);

  return (
    <Stack className={classes.breadcrumbTreeViewContainer}>
      {showTransientBanner && lastNavigateFailed ? (
        <div className={classes.transientBanner}>
          <div className={classes.transientBannerContent}>
            <WarningIcon className={classes.checkIcon} />
            <Typography variant="bodySmall">Path not found in data store</Typography>
          </div>
          <div>
            <Typography variant="bodySmall">
              You can still use this path - it will be used as entered when you save.
            </Typography>
          </div>
        </div>
      ) : showPathNotFoundTyping ? (
        <div className={classes.transientBanner}>
          <div className={classes.transientBannerContent}>
            <WarningIcon className={classes.checkIcon} />
            <Typography variant="bodySmall">Path not found in data store</Typography>
          </div>
          <Typography variant="bodySmall">
            You can still use this path — it will be used as entered when you save.
          </Typography>
        </div>
      ) : null}
      <div
        style={{
          alignItems: isDestination ? 'flex-start' : 'center',
          flexDirection: isDestination ? 'column' : 'row',
        }}
        className={classes.breadcrumbTreeViewHeader}
      >
        <div
          style={{
            display: isDestination ? 'flex' : 'block',
            alignItems: isDestination ? 'center' : 'flex-start',
            borderBottom: isDestination ? '1px solid var(--color-greyscale-100)' : 'none',
          }}
          className={classes.breadcrumbTreeViewHeaderContent}
        >
          <div style={{ flex: isDestination ? '1 1 auto' : '0 0 auto' }}>
            {showBreadcrumbs ? (
              <Breadcrumbs
                path={path}
                onBreadcrumbClick={handleBreadcrumbClick}
                onEditClick={handleEditClick}
                showEditIcon={!(isIDBased && pathInputReadOnly)}
              />
            ) : (
              <div className={classes.pathInputContainerWrapper}>
                <div className={classes.pathInputContainer}>
                  <SelectedLocationPathInput
                    readOnly={pathInputReadOnly}
                    placeholder={pathInputPlaceholder || 'Type the path here'}
                    onChange={handlePathInputChange}
                    value={showBreadcrumbs ? pathInputValue : pathInputDraft}
                    onSubmit={handleCheckClickWithNavigation}
                  />
                </div>
                {!showTransientBanner ? (
                  <PathWarnings
                    showBreadcrumbs={showBreadcrumbs}
                    isSelectingFromList={isSelectingFromList}
                    isResolvingPath={isResolvingPath}
                    isLoadingData={isLoadingData || false}
                    draftOrCommitted={draftOrCommitted}
                    hasInitialRoot={hasInitialRoot}
                    rootSegValid={rootSegValid}
                    lastNavigateFailed={lastNavigateFailed}
                    path={path as any}
                    currentData={currentData as any}
                    onCheckClick={handleCheckClickWithNavigation}
                  />
                ) : null}
              </div>
            )}
          </div>
          {isDestination ? (
            <div className={`${classes.addressBarContainer} address-bar-container`}>
              <Typography variant="bodySmall">/</Typography>
              <MentionsBasicEditor
                key={stateUpdatedAt}
                style={{ minWidth: 200 }}
                isSingleLine={true}
                allSuggestions={allPrefixSuggestions}
                editorState={customPrefixEditorState}
                suggestions={filteredPrefixSuggestions}
                onChange={handlePrefixEditorChange}
                onSearchChange={handlePrefixEditorSearch}
                onReturn={handlePrefixEditorKeyDown}
                groupByKey={'type'}
                groupDisplayNameKey={'type_display'}
                groupByEntryComponent={<div />}
                placeholder="Optionally add directories to the destination path, e.g., {yyyy}/{MM}/{dd}"
              />
            </div>
          ) : null}
        </div>
        <div
          style={{
            alignItems: isDestination ? 'center' : 'flex-start',
            alignSelf: isDestination ? 'flex-end' : 'auto',
          }}
          className={classes.breadcrumbTreeViewHeaderActions}
        >
          {onSearchChange && (
            <TextField
              value={search}
              placeholder="Find a Folder"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search.trim() ? (
                  <InputAdornment position="end">
                    <CancelIcon style={{ cursor: 'pointer' }} onClick={() => onSearchChange('')} />
                  </InputAdornment>
                ) : null,
              }}
              inputProps={{
                ['data-testid']: 'expandable-search-input',
              }}
              className={classes.searchInput}
              fullWidth
              onChange={(e) => onSearchChange(e.target.value)}
            />
          )}

          <div>
            {isLoadingProbeData ? (
              <RefreshIconRotate fontSize="large" className={classes.refreshIcon} />
            ) : (
              <RefreshIcon
                fontSize="large"
                className={classes.refreshIcon}
                onClick={handleRefreshClick}
              />
            )}
          </div>
        </div>
      </div>
      <Box className={classes.breadcrumbTreeViewContent}>
        {useEmptyFolderGate({
          shouldShowEmpty,
          childrenKnown: childrenStatus.childrenKnown,
          childCount: childrenStatus.childCount,
          isSelectingFromList,
          suppressEmptyUntilTs: suppressEmptyUntilRef.current,
          stableWindowMs: childrenStatus.childCount === 0 ? 0 : 200,
        }) ? (
          <Stack alignItems="center" justifyContent="center" padding={4} spacing={2}>
            <FolderOpenIcon
              style={{
                fontSize: '48px',
                color: 'var(--color-primary-500, #0050b3)',
              }}
            />
            <Typography variant="bodySmall">This folder is empty</Typography>
          </Stack>
        ) : (
          <FileList
            data={(() => {
              const resolved = lastNavigateFailed
                ? []
                : Array.isArray(currentData) &&
                    currentData.length === 0 &&
                    Array.isArray(lastNonEmptyDataRef.current)
                  ? (lastNonEmptyDataRef.current as any[])
                  : ((currentData as any[]) ?? []);
              // File-only listing (test-file picker): drop folder rows.
              return filesOnly ? resolved.filter((n: any) => n?.type !== 'folder') : resolved;
            })()}
            fileIngestModifiedAfterMs={fileIngestModifiedAfterMs}
            selectedNodeId={selectedNodeId}
            onNodePreview={onNodePreview}
            onNodeSelected={handleFolderClickWithSelection}
            onNodeExpandClicked={handleFolderNavigate}
            nodeKeyName={nodeKeyName}
            selectable={selectable}
            enableFileSelection={enableFileSelection}
            selectableFileMimeType={selectableFileMimeType}
            iconClasses={iconClasses}
            isLoadingData={isLoadingData}
            searchValue={search}
            onClearSearch={onSearchChange ? () => onSearchChange('') : undefined}
          />
        )}
      </Box>
    </Stack>
  );
};
